import React, { useEffect, useState } from "react";
import { brandingApi, apiError, printApi, apptApi } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Button, Field, Section, Loading } from "../../Components/UI/ui";
import { GATE, fileToImage, openPdf, todayStr, addDays, labName, MODALITIES, modalityLabel } from "../../lib/lab";

const lines = (arr) => (arr || []).join("\n");
const unlines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

// Settings → Letterhead (frontend guide §3): print layout, logo, header/footer
// lines, margins for pre-printed stationery, invoice tax details.
const Letterhead = () => {
  const { can } = useAuth();
  const { toast, branding, loadBranding, setBranding } = useApp();
  const ro = !can(...GATE.branding);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoBust, setLogoBust] = useState(0);
  const [logoBroken, setLogoBroken] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  useEffect(() => { loadBranding(); }, [loadBranding]);
  useEffect(() => {
    const b = branding?.branding || {};
    if (branding && !f) {
      setF({
        layout: b.layout || "branded",
        headerLines: lines(b.headerLines), footerLines: lines(b.footerLines),
        topMargin: b.letterhead?.topMargin ?? 130, bottomMargin: b.letterhead?.bottomMargin ?? 90,
        accentColor: b.accentColor || "#0F7B78", reportSignatureNote: b.reportSignatureNote || "",
        autoSendReportOnPublish: b.autoSendReportOnPublish !== false, reportWhatsAppTemplate: b.reportWhatsAppTemplate || "",
        autoSendBookingConfirmation: b.autoSendBookingConfirmation !== false,
        autoSendRadiologyReportOnPublish: b.autoSendRadiologyReportOnPublish,
        radiologyRooms: (b.radiologyRooms || []).map((r) => ({ ...r })),
        gstin: b.invoice?.gstin || "", pan: b.invoice?.pan || "", terms: b.invoice?.terms || "", bankDetails: b.invoice?.bankDetails || "",
      });
    }
  }, [branding, f]);

  const save = async () => {
    if (!/^#[0-9a-fA-F]{6}$/.test(f.accentColor)) return toast.error("Accent colour must be a hex value like #0F7B78");
    setSaving(true);
    try {
      const body = {
        layout: f.layout,
        headerLines: unlines(f.headerLines), footerLines: unlines(f.footerLines),
        letterhead: { topMargin: Number(f.topMargin) || 0, bottomMargin: Number(f.bottomMargin) || 0 },
        accentColor: f.accentColor, reportSignatureNote: f.reportSignatureNote.trim(),
        autoSendReportOnPublish: !!f.autoSendReportOnPublish,
        autoSendBookingConfirmation: !!f.autoSendBookingConfirmation,
        autoSendRadiologyReportOnPublish: f.autoSendRadiologyReportOnPublish,
        radiologyRooms: (f.radiologyRooms || []).filter((r) => r.code?.trim()).map((r) => ({ code: r.code.trim(), name: (r.name || "").trim(), modality: r.modality || "" })),
        reportWhatsAppTemplate: f.reportWhatsAppTemplate.trim() || null,
        invoice: { gstin: f.gstin.trim(), pan: f.pan.trim(), terms: f.terms.trim(), bankDetails: f.bankDetails.trim() },
      };
      const res = await brandingApi.update(body);
      setBranding(res?.branding ? res : { ...branding, branding: { ...(branding?.branding || {}), ...body } });
      toast.success("Letterhead saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const uploadLogo = async () => {
    setLogoBusy(true);
    try {
      const image = await fileToImage(logoFile, 2 * 1024 * 1024);
      await brandingApi.uploadLogo(image);
      setLogoFile(null); setLogoBust(Date.now()); setLogoBroken(false);
      toast.success("Logo uploaded");
    } catch (e) { toast.error(e); } finally { setLogoBusy(false); }
  };

  // Preview: open the most recent booking that has something published
  const preview = async (layout) => {
    setPreviewBusy(true);
    try {
      const res = await apptApi.list({ status: "REPORT_READY,COMPLETED", startDate: addDays(todayStr(), -90), endDate: todayStr(), limit: 50 });
      const appts = [...(res.appointments || [])].reverse();
      for (const a of appts) {
        if (a.serviceType !== "pathology") continue;
        try {
          const st = await printApi.reportStatus(a._id);
          if (st.canPrint) { openPdf(printApi.reportPdfUrl(a._id, { layout })); return; }
        } catch { /* try next */ }
      }
      toast.info("No published report in the last 90 days to preview — publish one first");
    } catch (e) { toast.error(e); } finally { setPreviewBusy(false); }
  };

  if (!f) return <Loading />;
  const hasLogo = !!branding?.branding?.logoImageId || !!logoBust;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Section title="Layout" right={<span className="lab-row"><Button size="sm" loading={previewBusy} onClick={() => preview("branded")}>Preview branded</Button><Button size="sm" loading={previewBusy} onClick={() => preview("letterhead")}>Preview letterhead</Button></span>}>
        <div className="lab-row" style={{ gap: 24 }}>
          <label className="lab-check"><input type="radio" disabled={ro} checked={f.layout === "branded"} onChange={() => set({ layout: "branded" })} /> <b>Branded</b>&nbsp;<span className="lab-muted">we print your logo, name, address and the lines below on every page</span></label>
          <label className="lab-check"><input type="radio" disabled={ro} checked={f.layout === "letterhead"} onChange={() => set({ layout: "letterhead" })} /> <b>Letterhead</b>&nbsp;<span className="lab-muted">you print on pre-printed stationery</span></label>
        </div>
        <div className="lab-muted" style={{ marginTop: 8 }}>
          Lab name, address and phone in the header come from the lab profile{labName(branding?.name) ? ` (${labName(branding.name)})` : ""}, not from this form.
        </div>
      </Section>

      {f.layout === "branded" ? (
        <Section title="Branded header & footer">
          <div className="lab-row" style={{ alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{ width: 160, height: 80, border: "1px dashed #989898", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", overflow: "hidden" }}>
              {hasLogo && !logoBroken ? <img alt="logo" src={brandingApi.logoUrl(logoBust)} style={{ maxWidth: "100%", maxHeight: "100%" }} onError={() => setLogoBroken(true)} /> : <span className="lab-muted" style={{ fontSize: 12 }}>No logo</span>}
            </div>
            {!ro && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <input type="file" accept="image/png,image/jpeg" className="lab-input" style={{ paddingTop: 7 }} onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
                <div className="lab-row"><Button size="sm" disabled={!logoFile} loading={logoBusy} onClick={uploadLogo}>Upload logo</Button><span className="lab-muted" style={{ fontSize: 12 }}>PNG/JPEG ≤ 2 MB</span></div>
              </div>
            )}
          </div>
          <div className="lab-grid cols-2">
            <Field label="Header lines" hint="One per line, e.g. NABL accredited (MC-1234), GSTIN"><textarea className="lab-textarea" disabled={ro} value={f.headerLines} onChange={(e) => set({ headerLines: e.target.value })} /></Field>
            <Field label="Footer lines" hint="Disclaimers, one per line"><textarea className="lab-textarea" disabled={ro} value={f.footerLines} onChange={(e) => set({ footerLines: e.target.value })} /></Field>
            <Field label="Accent colour" hint="Headings and table header text (hex)">
              <div className="lab-row"><input type="color" disabled={ro} value={/^#[0-9a-fA-F]{6}$/.test(f.accentColor) ? f.accentColor : "#0F7B78"} onChange={(e) => set({ accentColor: e.target.value.toUpperCase() })} style={{ width: 40, height: 40, border: "none", background: "none" }} /><input className="lab-input" disabled={ro} value={f.accentColor} onChange={(e) => set({ accentColor: e.target.value })} /></div>
            </Field>
            <Field label="Signature note" hint='Printed with the signature block, e.g. "Electronically verified"'><input className="lab-input" disabled={ro} value={f.reportSignatureNote} onChange={(e) => set({ reportSignatureNote: e.target.value })} /></Field>
          </div>
        </Section>
      ) : (
        <Section title="Pre-printed stationery margins">
          <div className="lab-grid cols-3">
            <Field label="Top margin (points)" hint="Space left blank for your printed header · 72 pt = 1 inch"><input type="number" min="0" className="lab-input" disabled={ro} value={f.topMargin} onChange={(e) => set({ topMargin: e.target.value })} /></Field>
            <Field label="Bottom margin (points)" hint="Space left blank for your printed footer"><input type="number" min="0" className="lab-input" disabled={ro} value={f.bottomMargin} onChange={(e) => set({ bottomMargin: e.target.value })} /></Field>
            <Field label="Signature note"><input className="lab-input" disabled={ro} value={f.reportSignatureNote} onChange={(e) => set({ reportSignatureNote: e.target.value })} /></Field>
          </div>
          <div className="lab-muted" style={{ marginTop: 8 }}>Print a test page on your stationery after saving and adjust until nothing overlaps.</div>
        </Section>
      )}

      <Section title="Messages to patients">
        <label className="lab-check">
          <input type="checkbox" disabled={ro} checked={!!f.autoSendBookingConfirmation} onChange={(e) => set({ autoSendBookingConfirmation: e.target.checked })} />
          <span><b>Send a WhatsApp confirmation when a booking is made</b></span>
        </label>
        <div className="lab-muted" style={{ marginTop: 8, marginBottom: 16 }}>
          {f.autoSendBookingConfirmation
            ? "The patient gets the date, time and where to come as soon as the booking is made — and when you accept a doctor's referral. Reminders are sent by hand from the booking."
            : "No confirmation is sent automatically. Send one from the booking with “Resend confirmation”."}
        </div>
        <label className="lab-check">
          <input type="checkbox" disabled={ro} checked={!!f.autoSendReportOnPublish} onChange={(e) => set({ autoSendReportOnPublish: e.target.checked })} />
          <span><b>Send the report on WhatsApp every time a test is published</b></span>
        </label>
        <div className="lab-muted" style={{ marginTop: 8 }}>
          {f.autoSendReportOnPublish
            ? "The patient gets the report as soon as a test is released — a partial report while others are pending, the final one at the end. If a send fails the publish still succeeds and the desk can resend from the booking."
            : "Reports are never sent automatically. Use “Send report” on the booking to send one."}
        </div>
        <label className="lab-check" style={{ marginTop: 14 }}>
          <input type="checkbox" disabled={ro} checked={f.autoSendRadiologyReportOnPublish ?? !!f.autoSendReportOnPublish}
            onChange={(e) => set({ autoSendRadiologyReportOnPublish: e.target.checked })} />
          <span>Do the same for <b>scan reports</b></span>
        </label>
        <div className="lab-muted" style={{ marginTop: 6 }}>
          Each scan is sent as it is released, so a booking with two scans sends two reports.
        </div>

        <div className="lab-grid cols-2" style={{ marginTop: 12 }}>
          <Field label="WhatsApp template" hint="Leave blank to use the standard lab_report template">
            <input className="lab-input" disabled={ro} placeholder="lab_report" value={f.reportWhatsAppTemplate} onChange={(e) => set({ reportWhatsAppTemplate: e.target.value })} />
          </Field>
        </div>
      </Section>

      <Section title="Scan rooms">
        <div className="lab-muted" style={{ marginBottom: 10 }}>
          Offered to the radiographer when a scan is marked performed. A label only — there is no slot or capacity logic behind it.
        </div>
        {(f.radiologyRooms || []).map((r, i) => (
          <div className="lab-row" key={i} style={{ marginBottom: 8 }}>
            <input className="lab-input sm" style={{ width: 120 }} placeholder="CT-1" disabled={ro} value={r.code || ""}
              onChange={(e) => set({ radiologyRooms: f.radiologyRooms.map((x, j) => (j === i ? { ...x, code: e.target.value } : x)) })} />
            <input className="lab-input sm" style={{ flex: 1 }} placeholder="CT room (ground floor)" disabled={ro} value={r.name || ""}
              onChange={(e) => set({ radiologyRooms: f.radiologyRooms.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
            <select className="lab-select sm" style={{ width: 180 }} disabled={ro} value={r.modality || ""}
              onChange={(e) => set({ radiologyRooms: f.radiologyRooms.map((x, j) => (j === i ? { ...x, modality: e.target.value } : x)) })}>
              <option value="">Any modality</option>
              {MODALITIES.map((m) => <option key={m} value={m}>{modalityLabel(m)}</option>)}
            </select>
            {!ro && <Button size="xs" variant="ghost" onClick={() => set({ radiologyRooms: f.radiologyRooms.filter((_, j) => j !== i) })}>✕</Button>}
          </div>
        ))}
        {!ro && <Button size="sm" onClick={() => set({ radiologyRooms: [...(f.radiologyRooms || []), { code: "", name: "", modality: "" }] })}>+ Add room</Button>}
        {(f.radiologyRooms || []).some((r) => !r.code?.trim()) && <div className="lab-danger-text" style={{ marginTop: 6 }}>Every room needs a code — blank rows are dropped on save.</div>}
      </Section>

      <Section title="Invoice details">
        <div className="lab-grid cols-2">
          <Field label="GSTIN" hint="Needed to print tax invoices"><input className="lab-input" disabled={ro} value={f.gstin} onChange={(e) => set({ gstin: e.target.value.toUpperCase() })} /></Field>
          <Field label="PAN"><input className="lab-input" disabled={ro} value={f.pan} onChange={(e) => set({ pan: e.target.value.toUpperCase() })} /></Field>
          <Field label="Payment terms"><input className="lab-input" disabled={ro} placeholder="Payable on receipt" value={f.terms} onChange={(e) => set({ terms: e.target.value })} /></Field>
          <Field label="Bank details" hint="Printed at the bottom of invoices"><textarea className="lab-textarea" disabled={ro} value={f.bankDetails} onChange={(e) => set({ bankDetails: e.target.value })} /></Field>
        </div>
      </Section>

      {!ro && <div className="lab-row end"><Button variant="primary" loading={saving} onClick={save}>Save letterhead</Button></div>}
    </div>
  );
};

export default Letterhead;
